import { createContext, useContext } from 'react';
import { Outlet } from 'react-router';
import {
  type IcdStatus,
  type ReconnectEpisode,
  type RecordingMetadata,
  type StartResult,
  type WebSocketErrorState,
  useRecordingWebSocket,
} from 'hooks/useRecordingWebSocket';
import type { ManualNotes } from 'hooks/useManualNotes';
import type { DiagnosedIcd } from 'types/result';

export type { RecordingMetadata };

interface RecordingSessionContextType {
  connect: () => Promise<void>;
  start: (metadata: RecordingMetadata) => Promise<StartResult>;
  pause: () => Promise<void>;
  sendChunk: (chunk: Blob) => Promise<void>;
  sendNotes: (notes: ManualNotes) => void;
  requestTranscription: () => Promise<void>;
  end: () => Promise<unknown>;
  disconnect: () => void;
  consumeServerError: () => WebSocketErrorState | null;
  isConnected: boolean;
  isProcessing: boolean;
  upstreamReconnect: ReconnectEpisode | null;
  icdCodes: DiagnosedIcd[] | null;
  icdStatus: IcdStatus;
  icdProcessingSlow: boolean;
  icdCodesReceivedAt: number | null;
  icdResultId: string | null;
}

const RecordingSessionContext = createContext<RecordingSessionContextType | null>(null);

export function RecordingSessionProvider() {
  const value = useRecordingWebSocket();

  return (
    <RecordingSessionContext.Provider value={value}>
      <Outlet />
    </RecordingSessionContext.Provider>
  );
}

export function useRecordingSession(): RecordingSessionContextType {
  const ctx = useContext(RecordingSessionContext);
  if (!ctx) {
    throw new Error('useRecordingSession must be used within RecordingSessionProvider');
  }
  return ctx;
}
